import React, { useId } from 'react';
import { Trans } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Box } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';

import RevampDatesPicker from './RevampDatesPicker';
import RevampDatesSaveButton from './RevampDatesSaveButton';
import useRevampDatesSelection, { DateRange, DateSelection } from './useRevampDatesSelection';

const MONTHS_SHOWN = 2;

export type { DateRange, DateSelection };

type Props = DateSelection & {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selection: DateSelection) => void;
  locale: Locale;
  allowsLooseDates?: boolean;
  allowsRecurring?: boolean;
};

const RevampDatesSheet = ({
  isOpen,
  onClose,
  onSave,
  locale,
  startDate,
  endDate,
  dates,
  isRecurring,
  weekDays,
  allowsLooseDates,
  allowsRecurring,
}: Props): JSX.Element => {
  const headingId = useId();
  const selection = useRevampDatesSelection({
    isOpen,
    onClose,
    onSave,
    startDate,
    endDate,
    dates,
    isRecurring,
    weekDays,
    allowsLooseDates,
    allowsRecurring,
  });

  return (
    <BottomSheet
      isOpen={isOpen}
      onRequestClose={onClose}
      headerId={headingId}
      // Repeating dates add a switch, the weekdays and a start date above the calendar,
      // which together outgrow the sheet: its content scrolls, and the button that saves
      // them stays pinned to the footer instead of being pushed off screen.
      overflowY="auto"
      FooterComponent={
        <Box pt="2x">
          <RevampDatesSaveButton selection={selection} />
        </Box>
      }
    >
      <A11yHiddenBox webAs="h2" id={headingId}>
        <Trans>Select dates</Trans>
      </A11yHiddenBox>
      <RevampDatesPicker
        selection={selection}
        locale={locale}
        allowsLooseDates={allowsLooseDates}
        allowsRecurring={allowsRecurring}
        monthsShown={MONTHS_SHOWN}
        isMobileBrowser
        testID="revamp-dates-sheet"
      />
    </BottomSheet>
  );
};

export default RevampDatesSheet;
